"use client";

import { useState, Suspense } from "react";
import Link from "next/link";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { Eye, EyeOff, Mail, Lock, AlertCircle } from "lucide-react";

function LoginForm() {
  const searchParams = useSearchParams();
  const redirectURL = searchParams.get("redirectURL");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLoginError, setShowLoginError] = useState(false);
  const [loginErrorText, setLoginErrorText] = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const router = useRouter();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setShowLoginError(false);
    const result = await signIn("credentials", {
      redirect: false,
      username: email,
      password: password,
    });

    // No error message received
    if (!result) {
      setLoginErrorText("Authentication failed. Please try again.");
      setShowLoginError(true);
      return;
    }

    // Error message received
    if (result.error) {
      setLoading(false);
      // Password change required, navigate to change password page
      if (result.error === "PasswordChangeRequired") {
        router.push(`/auth/change-password?email=${email}`);
        return;
      }

      if (result.error === "CredentialsSignin") {
        setLoginErrorText("Invalid email or password.");
        setShowLoginError(true);
        return;
      }

      setLoginErrorText(result.error);
      setShowLoginError(true);
      return;
    }

    // Redirect to the user's page when successfully signed in:
    setLoading(false);
    if (redirectURL) {
      router.push(redirectURL);
    } else {
      router.push("/platform");
    }
  };

  return (
    <div className="w-full max-w-md px-8">
      {/* Logo/Brand Section */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Welcome</h1>
        <p className="text-gray-400">Sign in to your account to continue</p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Input */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="password"
              type={showPassword ? "password" : "text"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {showLoginError && (
          <div className="flex items-start gap-3 p-4 bg-red-900/20 border border-red-800 rounded-lg animate-[shake_0.3s_ease-in-out]">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-400">{loginErrorText}</p>
            </div>
          </div>
        )}

        {/* Forgot Password Link */}
        <div className="flex items-center justify-between">
          <a
            href="/auth/forgot-password/request"
            className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
          >
            Forgot password?
          </a>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center items-center py-3 px-4 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold rounded-lg shadow-lg shadow-green-900/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Signing in...
            </>
          ) : (
            "Sign In"
          )}
        </button>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-700"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#1d232a] text-gray-400">Quick Links</span>
          </div>
        </div>

        {/* Additional Links */}
        <div className="flex flex-col gap-3">
          <a
            href="https://veriphytech.wetransfer.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-800 hover:bg-gray-750 text-gray-300 font-medium rounded-lg border border-gray-700 transition-all duration-200 hover:border-gray-600"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            Veriphy WeTransfer
          </a>
        </div>
      </form>
    </div>
  );
}

export default function Login() {
  return (
    <Suspense fallback={<div className="text-white">Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}
